/**
 * Excel Bulk Upload Component for Welcome Video
 * Upload multiple students via Excel for bulk welcome message generation
 */
import React, { useState, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Excel Template Download
const EXCEL_TEMPLATE_HEADERS = [
  'student_name', 'parent_name', 'parent_phone', 'class_name', 
  'section', 'roll_number', 'class_teacher', 'first_day_date'
];

const SAMPLE_DATA = [
  ['राहुल शर्मा', 'श्री शर्मा', '9876543210', 'Class 1', 'A', '1', 'Mrs. Sunita', '2025-04-01'],
  ['प्रिया गुप्ता', 'श्री गुप्ता', '9876543211', 'Class 2', 'B', '2', 'Mr. Amit', '2025-04-01'],
  ['आयुष पटेल', 'श्री पटेल', '9876543212', 'Class 1', 'A', '3', 'Mrs. Sunita', '2025-04-01'],
];

export default function BulkUploadWelcome({ token, schoolId, onComplete }) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Download Excel Template
  const downloadTemplate = () => {
    // Create CSV content
    const csvContent = [
      EXCEL_TEMPLATE_HEADERS.join(','),
      ...SAMPLE_DATA.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'welcome_video_template.csv';
    link.click();
  };

  // Parse CSV/Excel file
  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setError('File is empty or has no data rows');
          return;
        }

        // Get headers
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
        
        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length >= 3) { // At least name, parent, phone
            const row = {};
            headers.forEach((header, idx) => {
              row[header] = values[idx] || '';
            });
            
            // Validate required fields
            if (row.student_name && row.parent_phone) {
              data.push(row);
            }
          }
        }

        if (data.length === 0) {
          setError('No valid data found. Please check the file format.');
          return;
        }

        setParsedData(data);
        setError(null);
      } catch (err) {
        setError('Error parsing file: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setParsedData([]);
      setResults(null);
      setError(null);
      parseFile(selectedFile);
    }
  };

  // Process bulk upload
  const processBulkUpload = async () => {
    if (parsedData.length === 0) return;

    setUploading(true);
    setProgress({ current: 0, total: parsedData.length });
    setResults(null);

    try {
      // Format data for API
      const admissions = parsedData.map(row => ({
        school_id: schoolId || 'default',
        student_name: row.student_name,
        parent_name: row.parent_name || '',
        parent_phone: row.parent_phone,
        class_name: row.class_name || 'Class 1',
        section: row.section || '',
        roll_number: row.roll_number || '',
        class_teacher: row.class_teacher || '',
        first_day_date: row.first_day_date || ''
      }));

      // Call bulk generate API
      const res = await fetch(`${API_URL}/api/welcome-video/bulk-generate?school_id=${schoolId || 'default'}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(admissions)
      });

      const data = await res.json();
      
      if (data.success) {
        setResults(data);
        setProgress({ current: data.generated, total: data.total });
        onComplete?.(data);
      } else {
        setError('Bulk generation failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setError('Error processing bulk upload: ' + err.message);
    }

    setUploading(false);
  };

  // Clear everything
  const clearAll = () => {
    setFile(null);
    setParsedData([]);
    setResults(null);
    setError(null);
    setProgress({ current: 0, total: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            📊 Bulk Upload (Excel/CSV)
          </h3>
          <p className="text-sm text-gray-500">50+ students एक साथ upload करें</p>
        </div>
        <button
          onClick={downloadTemplate}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
        >
          📥 Download Template
        </button>
      </div>

      {/* Upload Area */}
      {!file && (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-all"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
          />
          <span className="text-4xl">📂</span>
          <h4 className="font-semibold mt-4">Click to Upload or Drag & Drop</h4>
          <p className="text-sm text-gray-500 mt-2">
            Supported: CSV, Excel (.xlsx, .xls)
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Max 500 students per upload
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <span className="text-2xl">❌</span>
          <div>
            <p className="font-semibold text-red-700">Error</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
          <button onClick={clearAll} className="ml-auto text-red-500 hover:text-red-700">
            Try Again
          </button>
        </div>
      )}

      {/* Parsed Data Preview */}
      {parsedData.length > 0 && !results && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">✅</span>
              <div>
                <p className="font-semibold">{file?.name}</p>
                <p className="text-sm text-gray-500">{parsedData.length} students found</p>
              </div>
            </div>
            <button onClick={clearAll} className="text-gray-400 hover:text-gray-600">
              ✕ Clear
            </button>
          </div>

          {/* Data Preview Table */}
          <div className="bg-gray-50 rounded-xl p-4 overflow-x-auto">
            <h4 className="font-semibold mb-3">Preview (First 5 rows)</h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pb-2">#</th>
                  <th className="pb-2">Student</th>
                  <th className="pb-2">Parent</th>
                  <th className="pb-2">Phone</th>
                  <th className="pb-2">Class</th>
                </tr>
              </thead>
              <tbody>
                {parsedData.slice(0, 5).map((row, idx) => (
                  <tr key={idx} className="border-t border-gray-200">
                    <td className="py-2">{idx + 1}</td>
                    <td className="py-2 font-medium">{row.student_name}</td>
                    <td className="py-2">{row.parent_name}</td>
                    <td className="py-2">{row.parent_phone}</td>
                    <td className="py-2">{row.class_name}{row.section ? `-${row.section}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parsedData.length > 5 && (
              <p className="text-center text-gray-400 text-sm mt-2">
                ... and {parsedData.length - 5} more
              </p>
            )}
          </div>

          {/* Process Button */}
          <button
            onClick={processBulkUpload}
            disabled={uploading}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <span className="animate-spin">⏳</span>
                Processing {progress.current}/{progress.total}...
              </>
            ) : (
              <>
                🚀 Generate {parsedData.length} Welcome Messages
              </>
            )}
          </button>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-4xl">🎉</span>
              <div>
                <h4 className="font-bold text-green-700 text-lg">Bulk Generation Complete!</h4>
                <p className="text-green-600">
                  {results.generated}/{results.total} messages generated successfully
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{results.generated}</p>
                <p className="text-xs text-gray-500">Success</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-500">{results.failed}</p>
                <p className="text-xs text-gray-500">Failed</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{results.total}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>

            {/* Individual Results */}
            {results.results && (
              <div className="max-h-40 overflow-y-auto bg-white rounded-lg p-3">
                <h5 className="font-semibold text-sm mb-2">Details:</h5>
                {results.results.map((r, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm py-1 border-b last:border-0">
                    <span>{r.success ? '✅' : '❌'}</span>
                    <span className="font-medium">{r.student_name}</span>
                    {!r.success && <span className="text-red-500 text-xs">({r.error})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={clearAll}
              className="flex-1 py-3 bg-gray-100 rounded-xl hover:bg-gray-200 font-semibold"
            >
              📂 Upload Another
            </button>
            <button
              className="flex-1 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 font-semibold"
              onClick={() => {
                // Open WhatsApp bulk sender (future feature)
                alert('Bulk WhatsApp feature coming soon!');
              }}
            >
              💬 Send All via WhatsApp
            </button>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <h4 className="font-semibold text-blue-800 mb-2">📋 Instructions</h4>
        <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
          <li>Download template using "Download Template" button</li>
          <li>Fill student details in Excel/CSV format</li>
          <li>Required columns: <code className="bg-blue-100 px-1 rounded">student_name</code>, <code className="bg-blue-100 px-1 rounded">parent_phone</code></li>
          <li>Upload file and click "Generate"</li>
          <li>Share messages via WhatsApp</li>
        </ol>
      </div>
    </div>
  );
}
